import { servicePost } from "./client.ts";
import { batchedServicePost } from "./readBatch.ts";
import type { ReturnObject } from "../types/returnObject.ts";

/* ── Enums ───────────────────────────────────────────────── */

export enum PackageDealMode {
  Standard = 0,
  Subsidize = 1,
  InteractiveExperience = 2,
}

export enum PackageDealType {
  Percent = 1,
  FlatReduction = 2,
  FlatPrice = 3,
  Subsidy = 4,
  Interactive = 5,
}

export enum PackageDealTimeType {
  Sunday = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
  Weekdays = 10,
  Weekends = 20,
  Any = 40,
}

/* ── Interfaces ──────────────────────────────────────────── */

export interface PackageDealTypeArgs {
  value: number;
  minReduction?: number | null;
  maxReduction?: number | null;
  minAmount?: number | null;
  maxAmount?: number | null;
  minCart?: number | null;
  maxCart?: number | null;
}

export interface PackageDealGroup {
  i: number | null;
  n: string;
  p: number[] | Record<number, boolean>;
  t: number[] | Record<number, boolean>;
  l: number[] | Record<number, boolean>;
  s: number | null;
  /** Free mix: the group's `i` units may be any mix of its products (A+A, A+B, …) rather than
   *  `i` of the same product. Standard deals with `i` > 1 only, and stored only when true, so
   *  every other group's JSON is unchanged. Older kiosks ignore it and use one-product rules. */
  m?: boolean;
}

export interface PackageDealTime {
  d: PackageDealTimeType;
  s: string;
  e: string;
}

export interface PackageDealLocationAccess {
  lists?: Record<number, boolean> | null;
  locations?: Record<number, boolean> | null;
}

export interface PackageDeal {
  id: number;
  title: string;
  imagePath: string;
  validStart: string | null;
  validEnd: string | null;
  bias: number;
  validDays: PackageDealTime[];
  type: PackageDealType;
  typeArgs: PackageDealTypeArgs | number[] | string;
  groups: PackageDealGroup[];
  membership: string | null;
  operatorId?: number | null;
  locationAccess?: PackageDealLocationAccess | null;
  externalReference?: string;
  fullIconImagePath?: string;
  appIconUrl?: string;
}

export interface ItemList {
  id: number;
  name?: string;
  type: string;
  items: number[];
}

/* ── LocationAccess serialization ────────────────────────── */

function mapToObj(items: string[]): Record<number, boolean> {
  const out: Record<number, boolean> = {};
  for (const item of items) {
    const n = parseInt(item, 10);
    if (!isNaN(n)) out[n] = true;
  }
  return out;
}

export function locationAccessFromString(raw: string | null | undefined): PackageDealLocationAccess | null {
  if (!raw) return null;
  const match = /^l:(?:,((?:[0-9],??)+),)? \| g:(?:,((?:[0-9],??)+),)?$/gi.exec(raw);
  if (match) {
    const locations = match[1]?.split(",").filter(Boolean);
    const lists = match[2]?.split(",").filter(Boolean);
    return {
      lists: lists?.length ? mapToObj(lists) : null,
      locations: locations?.length ? mapToObj(locations) : null,
    };
  }
  return {};
}

export function locationAccessAsString(item: PackageDealLocationAccess | null | undefined): string | null {
  if (!item) return null;
  const locations = item.locations ? Object.keys(item.locations) : null;
  const lists = item.lists ? Object.keys(item.lists) : null;
  return `l:${locations && locations.length > 0 ? `,${locations.join(",")},` : ""} | g:${lists && lists.length > 0 ? `,${lists.join(",")},` : ""}`;
}

/* ── Parse helpers ───────────────────────────────────────── */

/** Normalize a deal's `typeArgs` (object, legacy array, or JSON string) into the
 *  object form. Shared by the deal editor and the Issa chat action mapper. */
export function parseTypeArgs(raw: PackageDealTypeArgs | number[] | string): PackageDealTypeArgs {
  if (typeof raw === "string") {
    try { raw = JSON.parse(raw); } catch { return { value: 0 }; }
  }
  if (Array.isArray(raw)) {
    // Legacy format: [value, minReduction?, maxReduction?, minAmount?, maxAmount?]
    return {
      value: raw[0] ?? 0,
      minReduction: raw.length > 2 ? (raw[1] ?? null) : null,
      maxReduction: raw.length > 2 ? (raw[2] ?? null) : null,
      minAmount: raw.length > 3 ? (raw[3] ?? null) : null,
      maxAmount: raw.length > 4 ? (raw[4] ?? null) : null,
      minCart: null,
      maxCart: null,
    };
  }
  if (raw && typeof raw === "object" && (raw as PackageDealTypeArgs).value !== undefined) {
    return raw as PackageDealTypeArgs;
  }
  return { value: 0 };
}

function parseDeal(deal: PackageDeal): PackageDeal {
  if (typeof deal.typeArgs === "string") {
    deal.typeArgs = JSON.parse(deal.typeArgs);
  }
  if (typeof deal.groups === "string") {
    deal.groups = JSON.parse(deal.groups);
  }
  if (typeof deal.validDays === "string") {
    deal.validDays = JSON.parse(deal.validDays);
  }
  if (typeof deal.locationAccess === "string") {
    deal.locationAccess = locationAccessFromString(deal.locationAccess as unknown as string);
  }
  return deal;
}

function serializeDeal(deal: PackageDeal): Record<string, unknown> {
  const out: Record<string, unknown> = { ...deal };
  out.typeArgs = JSON.stringify(deal.typeArgs);
  out.groups = JSON.stringify(deal.groups);
  out.validDays = JSON.stringify(deal.validDays);
  out.locationAccess = locationAccessAsString(deal.locationAccess);
  return out;
}

/* ── API functions ───────────────────────────────────────── */

export async function getPackageDeal(id: number): Promise<ReturnObject<PackageDeal>> {
  const result = await batchedServicePost<PackageDeal>("GetPackageDeal", { id });
  if (result.oData) {
    result.oData = parseDeal(result.oData);
  }
  return result;
}

export async function savePackageDeal(deal: PackageDeal): Promise<ReturnObject<PackageDeal>> {
  const serialized = serializeDeal(deal);
  const result = await servicePost<PackageDeal>("SavePackageDeal", { deal: serialized });
  if (result.oData) {
    result.oData = parseDeal(result.oData);
  }
  return result;
}

/**
 * Commit an already-uploaded temp image (from the standard upload endpoint) into final
 * picture storage for a package deal. Returns the stored NAME for a kiosk image (goes into
 * `imagePath`) or the public URL for the app icon (goes into `appIconUrl`). Replaces the old
 * inline-base64 channel — the deal save then carries only references, not image bytes.
 */
export function commitPackageDealImage(
  tempFilename: string,
  imageName: string,
  operatorId: number | null,
  isAppIcon: boolean,
): Promise<ReturnObject<string>> {
  return servicePost<string>("WebCommitPackageDealImage", { tempFilename, imageName, operatorId, isAppIcon });
}

export async function getItemLists(): Promise<ReturnObject<ItemList[]>> {
  return batchedServicePost<ItemList[]>("WebGetItemLists", {});
}

export async function getMembershipName(publicId: string): Promise<ReturnObject<string>> {
  return servicePost<string>("PackageDeal_MembershipName", { publicId });
}

/* ── FlexyBox closed-loop membership types ──────────────── */

export interface FlexyBoxMembershipType {
  Id: string;
  Name: string;
}

/** Membership types from the FlexyBox CRM, using the closed-loop credentials
 *  configured on the active endpoint's kiosks. Throws when the endpoint has no
 *  FlexyBox reader or the CRM call fails. */
export async function getFlexyBoxMembershipTypes(): Promise<FlexyBoxMembershipType[]> {
  const r = await servicePost<FlexyBoxMembershipType[]>("WebGetFlexyBoxMembershipTypes", {});
  if (r.bError) throw new Error(r.sMessage ?? "Failed to load FlexyBox membership types");
  return r.oData ?? [];
}

/** Distinct closed-loop reader names configured on the active endpoint's kiosks
 *  (uppercase, for display — lowercase them when building references). */
export async function getClosedLoopReaders(): Promise<string[]> {
  const r = await servicePost<string[]>("WebGetClosedLoopReaders", {});
  if (r.bError) throw new Error(r.sMessage ?? "Failed to load closed-loop readers");
  return r.oData ?? [];
}
